import * as React from 'react';
import type { TFunction } from 'i18next';
import { toast } from '@ooh/ui/components/sonner';
import { QuickAddOfferToast } from './quick-add-offer-toast';

export interface QuickAddSuccessToastArgs {
  offerId: string;
  offerName: string;
  addedCount: number;
  skippedCount: number;
  totalCount: number;
}

/**
 * Shared with create-offer-page.tsx (CU-86cb1nqwv): `addUnitsToOffer`'s
 * response can resolve successfully while still reporting some units
 * skipped server-side — both call sites reuse this to render the same
 * skipped-units message instead of reimplementing it.
 */
export function buildQuickAddToastMessage(t: TFunction, args: QuickAddSuccessToastArgs) {
  if (args.addedCount === 0 && args.skippedCount > 0) {
    return t('quickAdd.toast.allSkipped', {
      offerName: args.offerName,
      skippedCount: args.skippedCount,
      totalCount: args.totalCount,
    });
  }

  return t('quickAdd.toast.standard', {
    addedCount: args.addedCount,
    offerName: args.offerName,
    skippedCount: args.skippedCount,
    totalCount: args.totalCount,
  });
}

export function showQuickAddToast(
  t: TFunction,
  args: QuickAddSuccessToastArgs,
  openOffer: (offerId: string) => void,
  options?: {
    /**
     * Omit the "Otwórz ofertę" action — used for a `creating`-mode commit
     * that already auto-navigated the caller to the new offer, so the
     * action would be redundant (CU-86cbc2vj1).
     */
    includeOpenOfferAction?: boolean;
  },
) {
  const includeOpenOfferAction = options?.includeOpenOfferAction ?? true;
  toast.custom(() =>
    React.createElement(QuickAddOfferToast, {
      message: buildQuickAddToastMessage(t, args),
      tone: args.addedCount === 0 && args.skippedCount > 0 ? 'warning' : 'success',
      ...(includeOpenOfferAction
        ? { actionLabel: t('quickAdd.toast.openOffer'), onAction: () => openOffer(args.offerId) }
        : {}),
    }),
  );
}

/**
 * Warn-only: added units may have reservations overlapping the offer's
 * default campaign date range — the add itself always succeeds
 * (add-units-conflict-warning). Shared with create-offer-page.tsx
 * (CU-86cb1nqwv) so both call sites use the same warning logic.
 */
export function warnOnAddConflicts(t: TFunction, conflictedMediaUnitIds: number[] | undefined) {
  const count = conflictedMediaUnitIds?.length ?? 0;
  if (count === 0) return;
  toast.warning(t('offerUnits.addConflictWarning', { count }));
}
